/**
 * A school code the learner arrived with but hasn't redeemed yet.
 *
 * The redeem link (`/redeem?code=…`) goes out by email, and most people who
 * click it have no account yet. Between the click and the redeem sit a signup
 * and an activation email, and the activation link opens in whatever browser
 * the mail client picks, often not the one that signed up. So the code rides
 * two carriers:
 *
 *   - localStorage, for the same-browser case (and for a signed-in visitor
 *     who wanders off before pressing Redeem);
 *   - the auth user's metadata, written at signup (`school_code`), which
 *     survives the hop to another browser because it lives on the account.
 *
 * Either one only PREFILLS the form; `redeem_school_pass` still validates.
 *
 * The local copy is BOUND to the first account that reads it while signed in,
 * and ignored by every other account, and it is cleared on sign-out. Without
 * that, a code left on a shared computer would surface for (or be attached at
 * signup to) the next learner who uses it. An unbound record is the anonymous
 * visitor who clicked the link: whoever signs up or in next on this browser
 * claims it, which is the flow the link exists for.
 * Every storage access is wrapped: private windows and blocked site data must
 * never break auth or the home page over a convenience.
 */

const KEY = 'kula_pending_school_code';
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
/** The metadata field register() writes at signup. */
export const SCHOOL_CODE_METADATA_KEY = 'school_code';

/** Codes are stored uppercased and trimmed — the same normalisation the
 *  redeem RPC applies — and capped so a crafted URL can't park a novel. */
export function normaliseSchoolCode(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  if (!code || code.length > 64) return null;
  return code;
}

export function rememberPendingSchoolCode(raw: string): void {
  const code = normaliseSchoolCode(raw);
  if (!code) return;
  try {
    // A fresh link always starts unbound: the person holding it now may not be
    // whoever was signed in before.
    localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() }));
  } catch {
    /* storage unavailable — signup metadata still carries it */
  }
}

/**
 * The locally remembered code, or null if none/expired/someone else's.
 *
 * `userId` is the signed-in account (null before signup). A signed-in read of
 * an unbound record binds it to that account; a record bound to a different
 * account is invisible. Does not clear.
 */
export function readPendingSchoolCode(userId: string | null): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { code, at, owner } = JSON.parse(raw) as { code?: unknown; at?: unknown; owner?: unknown };
    if (typeof at !== 'number' || Date.now() - at > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    const normalised = normaliseSchoolCode(typeof code === 'string' ? code : null);
    if (!normalised) return null;
    if (typeof owner === 'string') return owner === userId ? normalised : null;
    if (userId) localStorage.setItem(KEY, JSON.stringify({ code: normalised, at, owner: userId }));
    return normalised;
  } catch {
    return null;
  }
}

/**
 * Forget the local code. With `onlyCode`, only when it is that code: redeeming
 * one code must not throw away a different one still waiting.
 */
export function clearPendingSchoolCode(onlyCode?: string | null): void {
  try {
    if (onlyCode !== undefined) {
      const raw = localStorage.getItem(KEY);
      const stored = raw ? normaliseSchoolCode((JSON.parse(raw) as { code?: string }).code) : null;
      if (stored !== normaliseSchoolCode(onlyCode)) return;
    }
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
